import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import L, { CRS, Icon, LatLngTuple } from 'leaflet';
import { MapContainer, ImageOverlay, useMap, Polygon, SVGOverlay } from 'react-leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';
import { useLocation, useNavigate } from 'react-router-dom';

import { useMapState } from './hooks/useMapState';
import { MapController } from './components/MapController';
import { PlanSelector } from './components/PlanSelector';
import { LoadingOverlay } from './components/LoadingOverlay';
import DeviceMarker from './customMarker/DeviceMarker';
import 'leaflet/dist/leaflet.css';
import './InteractiveMap.css';
import AddMenuButton from './components/AddMenuButton';
import AddMCTModal from './components/Modals/AddMCTModal';
import AddSRBMModal from './components/Modals/AddSRBMModal';
import AddHubModal from './components/Modals/AddHubModal';
import MapSettings from './components/MapSettings';
import LocationSettings from './components/LocationSettings';
import HeatmapLayer from './components/HeatmapLayer';
import { useGetHeatMapTrapData } from './hooks/useGetHeatMapTrapData';
import useGetHeatMapSBSData from './hooks/useGetHeatMapSBSData';
import MapLegend from './components/MapLegend/MapLegend';
import AddVulOnMapModal from './components/Modals/AddVulOnMap/AddVulOnMapModal';
import AddEncounterOnMapModal from './components/Modals/AddEncOnMap/AddEncounterOnMapModal';
import MapUtilityButtons from './components/MapUtilityButtons';
import PutEncAfterBlitzModal from './components/Modals/PutEncAfterBlitzModal/PutEncAfterBlitzModal';
import CompleteEncModal from './components/Modals/CompleteEncModal/CompleteEncModal';
import AddTrapModal from './components/Modals/AddTrapModal';
import MapViewController from './components/MapSupportComponents/MapViewController';
import CompleteVulModal from './components/Modals/CompleteVulModal/CompleteVulModal';
import EditPlanOnMapModal from './components/Modals/EditPlanOnMap/EditPlanOnMapModal';
import AreaSelectionControl from './components/AreaSelectionControl';
import DeletePlanOnMapModal from './components/Modals/DeletPlanOnMap/DeletPlanOnMapModal';

import { useGetUnmountedDevices } from 'hooks/InteractiveMap/useGetUnmountedDevices';
import { useGetUnmountedHubs } from 'hooks/InteractiveMap/useGetUnmountedHubs';
import TrapDeleteModal from 'components/DeleteTrapModal/TrapsDeleteModal';
import HubDeleteModal from 'components/DeleteHubModal/HubsDeleteModal';
import {
  Device,
  SignType,
  VulnerabilityOnMap,
  EncounterOnMap,
  BaitHistoryTrigger,
  CatchHistoryTrigger,
  HistoryTrigger,
} from 'api/InteractiveMap/types';
import { TrapTableTypeLink } from 'components/TrapsTables/trapTables';
import { useGetUnmountedSigns } from 'hooks/InteractiveMap/useGetUnmountedSigns';
import { truncateText } from 'utils/truncateText';
import { MapLocation } from 'api/traps/types';

// Fix Leaflet's default icon
const DefaultIcon = Icon.Default.prototype;
DefaultIcon.options.iconUrl = icon;
DefaultIcon.options.iconRetinaUrl = iconRetina;
DefaultIcon.options.shadowUrl = iconShadow;
DefaultIcon.options.iconSize = [25, 41];
DefaultIcon.options.iconAnchor = [12, 41];
DefaultIcon.options.popupAnchor = [1, -34];
DefaultIcon.options.tooltipAnchor = [16, -28];
DefaultIcon.options.shadowSize = [41, 41];

type AnyTriggerType = HistoryTrigger | CatchHistoryTrigger | BaitHistoryTrigger;

interface BestPairData {
  slug: string;
  side: string | null;
  score: number;
  trippedCount: number;
  lastCapture1: string | null;
  lastCapture2: string | null;
  lastEmpty1: string | null;
  lastEmpty2: string | null;
  combineHistoryTriggers: AnyTriggerType[];
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const WaterRingEffect = ({ position, delay = 0 }: { position: [number, number]; delay?: number }) => {
  const map = useMap();
  const [showRing, setShowRing] = useState(false);
  const [ringPosition, setRingPosition] = useState<[number, number]>([0, 0]);

  useEffect(() => {
    if (position) {
      const timer = setTimeout(() => {
        const point = map.latLngToContainerPoint(position);
        setRingPosition([point.x, point.y]);
        setShowRing(true);

        const ringTimer = setTimeout(() => {
          setShowRing(false);
        }, 4300);

        return () => clearTimeout(ringTimer);
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [position, map, delay]);

  if (!showRing) return null;

  return (
    <>
      <div className="water-ring" style={{ left: ringPosition[0], top: ringPosition[1] }} />
      <div className="water-ring second" style={{ left: ringPosition[0], top: ringPosition[1] }} />
    </>
  );
};

const ZoomToMarker = ({ position, onFlyComplete }: { position: [number, number]; onFlyComplete?: () => void }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo(position, 2, { duration: 0.5 });

    const timer = setTimeout(() => {
      onFlyComplete?.();
    }, 500);

    return () => clearTimeout(timer);
  }, [position, map, onFlyComplete]);
  return null;
};

const ZoomToMarkerWithWaterRing = ({ position }: { position: [number, number] }) => {
  const [showWaterRing, setShowWaterRing] = useState(false);

  const handleFlyComplete = () => {
    setShowWaterRing(true);
  };

  return (
    <>
      <ZoomToMarker position={position} onFlyComplete={handleFlyComplete} />
      {showWaterRing && <WaterRingEffect position={position} />}
    </>
  );
};

const MapInteractionController = ({ isAnyModalOpen }: { isAnyModalOpen: boolean }) => {
  const map = useMap();

  useEffect(() => {
    if (isAnyModalOpen) {
      map.dragging.disable();
      map.touchZoom.disable();
      map.doubleClickZoom.disable();
      map.scrollWheelZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
    } else {
      map.dragging.enable();
      map.touchZoom.enable();
      map.doubleClickZoom.enable();
      map.scrollWheelZoom.enable();
      map.boxZoom.enable();
      map.keyboard.enable();
    }
  }, [isAnyModalOpen, map]);

  return null;
};

const InteractiveMap = () => {
  const {
    mapHeight,
    imageDimensions,
    plans,
    currentPlan,
    devices,
    plansLoading,
    isRefetchingPlans,
    devicesLoading,
    encountersLoading,
    vulnerabilitiesLoading,
    handlePlanSelect,
    selectedPlanSlug,
    isAddMctModalOpen,
    isAddSMSTModalOpen,
    isAddSRSTModalOpen,
    isAddSRBMModalOpen,
    isAddHubModalOpen,
    setIsAddMctModalOpen,
    setIsAddSMSTModalOpen,
    setIsAddSRSTModalOpen,
    setIsAddSRBMModalOpen,
    setIsAddHubModalOpen,
    slug,
    refetchDevices,
    refetchHubs,
    hubs,
    vulnerabilities,
    encounters,
    refetchVulnerabilities,
    refetchEncounters,
    isAnyModalOpen,
    isHeatmapPopupOpen,
    setIsHeatmapPopupOpen,
    isLocationSettingsOpen,
    setIsLocationSettingsOpen,
    tempPolygon,
    setTempPolygon,
    polygons,
    colorPolygon,
    setColorPolygon,
    editingIndex,
    setEditingIndex,
    isDrawingMode,
    setIsDrawingMode,
    setPolygons,
    refetchLocations,
    isActiveFilterArchive,
    setIsActiveFilterArchive,
    heatmapDateStart,
    setHeatmapDateStart,
    heatmapDateEnd,
    setHeatmapDateEnd,
    setArchiveMode,
  } = useMapState();

  const navigate = useNavigate();
  const location = useLocation();
  const [tempMarkers, setTempMarkers] = useState<{ position: LatLngTuple; device: any }[]>([]);
  const [isMapSettingsOpen, setIsMapSettingsOpen] = useState(false);
  const isArchiveFetchRef = useRef(false);
  const initialViewRef = useRef<{ center: [number, number]; zoom: number } | null>(null);

  if (!initialViewRef.current && imageDimensions) {
    initialViewRef.current = {
      center: [imageDimensions.height / 2, imageDimensions.width / 2],
      zoom: 2,
    };
  }

  const imageBounds: [[number, number], [number, number]] | undefined = useMemo(() => {
    if (!imageDimensions) return undefined;
    return [
      [0, 0],
      [imageDimensions.height, imageDimensions.width],
    ];
  }, [imageDimensions]);

  useEffect(() => {
    if (!devicesLoading && !encountersLoading && !vulnerabilitiesLoading) {
      isArchiveFetchRef.current = false;
    }
  }, [devicesLoading, encountersLoading, vulnerabilitiesLoading]);

  const markerRefs = useRef<Record<string, any>>({});
  const [hasZoomed, setHasZoomed] = useState(false);
  const query = useQuery();
  const deviceZoomSlug = query.get('device_zoom');
  const deviceToZoom = !hasZoomed
    ? Object.values(devices)
        .flat()
        .find(d => d.slug === deviceZoomSlug)
    : undefined;
  const encZoomId = query.get('encounter_zoom');
  const encToZoom = !hasZoomed ? encounters.find(e => e.id.toString() === encZoomId) : undefined;
  const deviceAddSlug = query.get('device_add_slug');
  const deviceAddType = query.get('device_type');
  const signAddId = query.get('sign_add_id');
  const signAddType = query.get('sign_type');

  useEffect(() => {
    if (currentPlan?.slug) {
      setIsDrawingMode(false);
      setTempPolygon([]);
      setEditingIndex(null);
    }
  }, [currentPlan?.slug, setIsDrawingMode, setTempPolygon, setEditingIndex]);

  const [filters, setFilters] = useState({
    mct: true,
    srbm: true,
    srst: true,
    smst: true,
    vulnerabilities: true,
    heatmap: false,
    archive: false,
    filter: false,
    encounters: true,
    heatmapMct: false,
    heatmapSRBM: false,
    heatmapSRST: false,
    heatmapSMST: false,
    srbmFilter: 'all',
    snapsFilter: 'all',
  });

  useEffect(() => {
    setFilters({
      mct: true,
      srbm: true,
      srst: true,
      smst: true,
      vulnerabilities: true,
      heatmap: false,
      archive: false,
      filter: false,
      encounters: true,
      heatmapMct: false,
      heatmapSRBM: false,
      heatmapSRST: false,
      heatmapSMST: false,
      srbmFilter: 'all',
      snapsFilter: 'all',
    });
  }, [currentPlan, slug]);

  type FiltersState = typeof filters;
  const formatHeatmapDate = (date: string, isEnd: boolean) => {
    if (heatmapDateStart === heatmapDateEnd) {
      return isEnd ? `${date}` : `${date}`;
    }
    return date;
  };
  const {
    data: heatmapData,
    refetch: refetchHeatmap,
    isFetching: isHeatmapLoading,
  } = useGetHeatMapTrapData(
    currentPlan?.slug || '',
    formatHeatmapDate(heatmapDateStart, false),
    formatHeatmapDate(heatmapDateEnd, true),
    'mct',
  );

  const {
    data: heatmapSRBMData,
    refetch: refetchHeatmapSRBM,
    isFetching: isHeatmapSRBMLoading,
  } = useGetHeatMapSBSData(
    currentPlan?.slug || '',
    formatHeatmapDate(heatmapDateStart, false),
    formatHeatmapDate(heatmapDateEnd, true),
  );

  const {
    data: heatmapSRSTData,
    refetch: refetchHeatmapSRST,
    isFetching: isHeatmapSRSTLoading,
  } = useGetHeatMapTrapData(
    currentPlan?.slug || '',
    formatHeatmapDate(heatmapDateStart, false),
    formatHeatmapDate(heatmapDateEnd, true),
    'srst',
  );

  const {
    data: heatmapSMSTData,
    refetch: refetchHeatmapSMST,
    isFetching: isHeatmapSMSTLoading,
  } = useGetHeatMapTrapData(
    currentPlan?.slug || '',
    formatHeatmapDate(heatmapDateStart, false),
    formatHeatmapDate(heatmapDateEnd, true),
    'smst',
  );

  const { data: unmountedDevices } = useGetUnmountedDevices(
    slug || '',
    deviceAddType as 'mct' | 'srbm' | 'smst' | 'srst',
  );
  const { data: unmountedHubs } = useGetUnmountedHubs(slug || '');
  const { data: unmountedSigns } = useGetUnmountedSigns(slug || '', signAddType as 'enc' | 'vul');

  // Виправлена та безпечна функція зміни фільтрів
  const handleFilterChange = useCallback(<K extends keyof FiltersState>(filter: K, value: FiltersState[K]) => {
    setFilters(prev => {
      if (filter === 'heatmap') {
        const boolValue = typeof value === 'string' ? value === 'true' : (value as boolean);
        return {
          ...prev,
          heatmap: boolValue,
          heatmapMct: boolValue,
          heatmapSRBM: boolValue,
          heatmapSMST: boolValue,
          heatmapSRST: boolValue,
        };
      }
      return { ...prev, [filter]: value };
    });
  }, []);

  const filteredDevices = Object.values(devices)
    .flat()
    .filter(device => {
      if (device.model.startsWith('srbm') && !filters.srbm) return false;
      if (device.model.startsWith('mct') && !filters.mct) return false;
      if (device.model.startsWith('srst') && !filters.srst) return false;
      if (device.model.startsWith('smst') && !filters.smst) return false;

      const triggers = device.history_triggers || [];

      if (device.model.startsWith('srbm')) {
        if (filters.srbmFilter === 'consumption') {
          const hasConsumption =
            triggers.some((t: any) => t.bait_eaten && t.bait_eaten > 0) || device.status === 'danger';
          if (!hasConsumption) return false;
        } else if (filters.srbmFilter === 'no_consumption') {
          const hasActivityNoConsumption =
            triggers.some((t: any) => Number(t.enter) > 0 && (!t.bait_eaten || t.bait_eaten === 0)) ||
            device.status === 'warning';
          if (!hasActivityNoConsumption) return false;
        } else if (filters.srbmFilter === 'any_activity') {
          const hasAnyActivity =
            triggers.some((t: any) => Number(t.enter) > 0 || (t.bait_eaten && t.bait_eaten > 0)) ||
            ['danger', 'warning'].includes(String(device.status));
          if (!hasAnyActivity) return false;
        }
      }

      if (device.model.startsWith('mct') || device.model.startsWith('srst') || device.model.startsWith('smst')) {
        if (filters.snapsFilter === 'captured') {
          const hasCatch = triggers.some((t: any) => t.status === 'danger') || device.status === 'danger';
          if (!hasCatch) return false;
        } else if (filters.snapsFilter === 'not_captured') {
          const hasEmptyTrigger = triggers.some((t: any) => t.status === 'warning') || device.status === 'warning';
          if (!hasEmptyTrigger) return false;
        } else if (filters.snapsFilter === 'any_activity') {
          const hasAnyTrigger =
            triggers.some((t: any) => t.status === 'danger' || t.status === 'warning') ||
            ['danger', 'warning'].includes(String(device.status));
          if (!hasAnyTrigger) return false;
        }
      }

      return true;
    });

  useEffect(() => {
    if (!plansLoading && (!plans || plans.length === 0)) {
      window.location.href = `/r/object/create-plan/${slug}?no_plan_redirect=true`;
    }
  }, [plansLoading, plans, slug]);

  useEffect(() => {
    if (devicesLoading || plansLoading || isRefetchingPlans) return;
    if (!deviceToZoom) return;

    const url = new URL(window.location.href);
    url.searchParams.delete('device_zoom');
    url.searchParams.delete('encounter_zoom');
    window.history.replaceState({}, '', url.toString());

    let attempts = 0;
    const maxAttempts = 10;
    const tryOpenPopup = () => {
      const marker = markerRefs.current[deviceToZoom.slug];
      if (marker && typeof marker.openPopup === 'function') {
        marker.openPopup();
        setTimeout(() => setHasZoomed(true), 5000);
      } else if (attempts < maxAttempts) {
        attempts += 1;
        setTimeout(tryOpenPopup, 200);
      }
    };

    setTimeout(tryOpenPopup, 700);
  }, [deviceToZoom, devicesLoading, plansLoading, isRefetchingPlans]);

  useEffect(() => {
    if (devicesLoading || plansLoading || isRefetchingPlans || encountersLoading) return;
    if (!encToZoom) return;

    const url = new URL(window.location.href);
    url.searchParams.delete('device_zoom');
    url.searchParams.delete('encounter_zoom');
    window.history.replaceState({}, '', url.toString());

    let attempts = 0;
    const maxAttempts = 10;
    const tryOpenPopup = () => {
      const marker = markerRefs.current[`enc_${encToZoom.id}`];
      if (marker && typeof marker.openPopup === 'function') {
        marker.openPopup();
        setTimeout(() => setHasZoomed(true), 5000);
      } else if (attempts < maxAttempts) {
        attempts += 1;
        setTimeout(tryOpenPopup, 200);
      }
    };

    setTimeout(tryOpenPopup, 700);
  }, [encToZoom, devicesLoading, plansLoading, isRefetchingPlans, encountersLoading]);

  useEffect(() => {
    if (tempMarkers.length > 0) {
      tempMarkers.forEach(tempMarker => {
        const marker = markerRefs.current[`temp_${tempMarker.device.slug}`];
        if (marker && typeof marker.openPopup === 'function') {
          marker.openPopup();
        }
      });
    }
  }, [tempMarkers]);

  useEffect(() => {
    if (filters.heatmap) {
      refetchHeatmap();
      refetchHeatmapSRBM();
      refetchHeatmapSRST();
      refetchHeatmapSMST();
    }
  }, [
    heatmapDateStart,
    heatmapDateEnd,
    filters.heatmap,
    refetchHeatmap,
    refetchHeatmapSRBM,
    refetchHeatmapSRST,
    refetchHeatmapSMST,
    currentPlan?.slug,
  ]);

  useEffect(() => {
    refetchDevices();
    refetchVulnerabilities();
    refetchEncounters();
    refetchHubs();
  }, [isActiveFilterArchive, refetchDevices, refetchVulnerabilities, refetchEncounters, refetchHubs]);

  const handleSetTempMarker = useCallback(
    (devices: any[] | any) => {
      if (!imageDimensions) return;

      const devicesArray = Array.isArray(devices) ? devices : [devices];
      const markerSize = 60;
      const spacing = 40;
      const columnSpacing = 60;
      const startX = -120;
      const startY = 0;

      const markersPerColumn = Math.floor((mapHeight - 40) / (markerSize + spacing));
      const currentMarkersCount = tempMarkers.length;

      const newMarkers = devicesArray.map((device, index) => {
        const totalIndex = currentMarkersCount + index;
        const column = Math.floor(totalIndex / markersPerColumn);
        const row = totalIndex % markersPerColumn;

        const x = startX - column * (markerSize + columnSpacing);
        const y = startY + row * (markerSize + spacing);
        const clampedY = Math.min(mapHeight - markerSize, y);

        return {
          position: [clampedY, x] as LatLngTuple,
          device,
        };
      });

      setTempMarkers(prev => [...prev, ...newMarkers]);
    },
    [imageDimensions, mapHeight, tempMarkers.length],
  );

  const addingRef = useRef(false);

  useEffect(() => {
    if (!deviceAddSlug || !deviceAddType) return;
    if (devicesLoading || plansLoading || isRefetchingPlans) return;
    if (addingRef.current) return;
    const alreadyInTemp = tempMarkers.some(m => m.device.slug === deviceAddSlug);
    if (alreadyInTemp) {
      navigate(location.pathname, { replace: true });
      return;
    }

    let foundDevice: any = null;
    if (['mct', 'srbm', 'smst', 'srst'].includes(deviceAddType)) {
      foundDevice = Object.values(devices)
        .flat()
        .find(d => d.slug === deviceAddSlug && d.model?.toLowerCase().startsWith(deviceAddType));
      if (!foundDevice && unmountedDevices) {
        foundDevice = unmountedDevices.find(d => d.slug === deviceAddSlug);
      }
    } else if (deviceAddType === TrapTableTypeLink.Hubs) {
      foundDevice = hubs.find(h => h.slug === deviceAddSlug) ?? unmountedHubs?.find(h => h.slug === deviceAddSlug);
    }
    if (!foundDevice) return;

    addingRef.current = true;
    const maybePromise = handleSetTempMarker(foundDevice);

    Promise.resolve(maybePromise)
      .then(() => {
        navigate(location.pathname, { replace: true });
      })
      .finally(() => {
        addingRef.current = false;
      });
  }, [
    deviceAddSlug,
    deviceAddType,
    devicesLoading,
    plansLoading,
    isRefetchingPlans,
    devices,
    hubs,
    unmountedDevices,
    unmountedHubs,
    tempMarkers,
    handleSetTempMarker,
    navigate,
    location.pathname,
  ]);

  useEffect(() => {
    if (!signAddId || !signAddType) return;
    if (encountersLoading || vulnerabilitiesLoading || plansLoading || isRefetchingPlans) return;
    if (addingRef.current) return;
    const alreadyInTemp = tempMarkers.some(m => m.device.slug === signAddId);

    if (alreadyInTemp) {
      navigate(location.pathname, { replace: true });
      return;
    }

    let foundSign: any = null;
    if (['enc', 'vul'].includes(signAddType)) {
      foundSign = Object.values(signAddType === SignType.vulnerability ? vulnerabilities : encounters)
        .flat()
        .find(d => d.id.toString() === signAddId);
      if (!foundSign && signAddType === 'vul' && (unmountedSigns as VulnerabilityOnMap[])) {
        foundSign = (unmountedSigns as VulnerabilityOnMap[]).find(s => s.id.toString() === signAddId);
      } else if (!foundSign && signAddType === 'enc' && (unmountedSigns as EncounterOnMap[])) {
        foundSign = (unmountedSigns as EncounterOnMap[]).find(s => s.id.toString() === signAddId);
      }
    }

    if (!foundSign) return;

    addingRef.current = true;
    const maybePromise = handleSetTempMarker({ ...foundSign, type: signAddType });
    Promise.resolve(maybePromise)
      .then(() => {
        navigate(location.pathname, { replace: true });
      })
      .finally(() => {
        addingRef.current = false;
      });
  }, [
    handleSetTempMarker,
    signAddId,
    signAddType,
    plansLoading,
    isRefetchingPlans,
    encounters,
    encountersLoading,
    vulnerabilities,
    vulnerabilitiesLoading,
    unmountedSigns,
    tempMarkers,
    navigate,
    location.pathname,
  ]);

  const isHardLoading = (devicesLoading || encountersLoading || vulnerabilitiesLoading) && !isArchiveFetchRef.current;

  if (plansLoading || isRefetchingPlans || !currentPlan || !imageDimensions || isHardLoading) {
    return (
      <div className="card">
        <div className="card-body position-relative">
          <LoadingOverlay mapHeight={mapHeight} />
        </div>
      </div>
    );
  }

  function MapWithRef(_props: any) {
    return null;
  }

  const handleTempMarkerSaved = async (deviceSlug?: string) => {
    navigate(location.pathname, { replace: true });
    setTempMarkers(prev => (deviceSlug ? prev.filter(m => m.device.slug !== deviceSlug) : []));
    await refetchDevices();
    await refetchHubs();
  };

  const STATUS_PRIORITY: Record<string, number> = {
    danger: 4,
    warning: 3,
    success: 2,
    dark: 1,
  };

  const isSnap = (m: string) => /^s[rm]st/i.test(m);
  // const isTripped = (s?: string | null) => s === TrapStatus.danger || s === TrapStatus.warning;
  const pairKey = (d: Device) => (isSnap(d.model) ? `${d.model}:${d.serial_number ?? d.slug}` : `single:${d.slug}`);

  const getSnapIndex = (d: any): 1 | 2 | null => {
    if (d.snap === 1 || d.snap === 2) return d.snap;
    const side = d.trap_connected_module?.toUpperCase();
    if (side === 'L') return 1;
    if (side === 'R') return 2;
    return null;
  };

  const newer = (a?: string | null, b?: string | null) => {
    if (!a) return b ?? null;
    if (!b) return a ?? null;
    return new Date(a) >= new Date(b) ? a : b;
  };
  const isCaptureTrigger = (status?: string | null) => {
    if (!status) return false;
    const s = String(status).toUpperCase();
    return s === 'DANGER' || s === 'APPROVED_MICE' || s === 'APPROVED_NOT_MICE';
  };

  const isEmptyCatchTrigger = (status?: string | null) => {
    if (!status) return false;
    const s = String(status).toUpperCase();
    return s === 'WARNING' || s === 'CAUGHT_EMPTY';
  };
  const bestByPair = filteredDevices.reduce((acc, d: any) => {
    if (!isSnap(d.model)) return acc;

    const baseKey = pairKey(d);
    const key = d.place_point_id ? `${baseKey}_${d.place_point_id}` : baseKey;

    const score =
      (STATUS_PRIORITY[String(d.status).toLowerCase()] ?? 0) * 10 +
      ((d.trap_connected_module ?? '').toUpperCase() === 'R' ? 1 : 0);

    const prev = acc.get(key) ?? {
      slug: '',
      side: null as string | null,
      score: -Infinity,
      trippedCount: 0,
      lastCapture1: null as string | null,
      lastCapture2: null as string | null,
      lastEmpty1: null as string | null,
      lastEmpty2: null as string | null,
      combineHistoryTriggers: [] as AnyTriggerType[],
    };

    const isDeviceTripped = ['danger', 'warning'].includes(String(d.status).toLowerCase());
    const trippedCount = prev.trippedCount + (isDeviceTripped ? 1 : 0);
    const snapIdx = getSnapIndex(d);

    const triggers = d.history_triggers ?? [];

    const lastCapGeneral = triggers.find((t: any) => isCaptureTrigger(t.status))?.date_time;
    const lastEmpGeneral = triggers.find((t: any) => isEmptyCatchTrigger(t.status))?.date_time;

    const lastCapL = triggers.find((t: any) => isCaptureTrigger(t.status) && t.enter?.toUpperCase() === 'L')?.date_time;
    const lastEmpL = triggers.find(
      (t: any) => isEmptyCatchTrigger(t.status) && t.enter?.toUpperCase() === 'L',
    )?.date_time;
    const lastCapR = triggers.find((t: any) => isCaptureTrigger(t.status) && t.enter?.toUpperCase() === 'R')?.date_time;
    const lastEmpR = triggers.find(
      (t: any) => isEmptyCatchTrigger(t.status) && t.enter?.toUpperCase() === 'R',
    )?.date_time;

    let next = { ...prev, trippedCount };

    if (snapIdx === 1) {
      next.lastCapture1 = newer(prev.lastCapture1, lastCapGeneral ?? null);
      next.lastEmpty1 = newer(prev.lastEmpty1, lastEmpGeneral ?? null);
    } else if (snapIdx === 2) {
      next.lastCapture2 = newer(prev.lastCapture2, lastCapGeneral ?? null);
      next.lastEmpty2 = newer(prev.lastEmpty2, lastEmpGeneral ?? null);
    } else {
      next.lastCapture1 = newer(prev.lastCapture1, lastCapL ?? null);
      next.lastCapture2 = newer(prev.lastCapture2, lastCapR ?? null);
      next.lastEmpty1 = newer(prev.lastEmpty1, lastEmpL ?? null);
      next.lastEmpty2 = newer(prev.lastEmpty2, lastEmpR ?? null);
    }

    next.combineHistoryTriggers = [...next.combineHistoryTriggers, ...triggers];

    if (score > prev.score) {
      next = { ...next, slug: d.slug, side: d.trap_connected_module ?? null, score };
    }

    acc.set(key, next);
    return acc;
  }, new Map<string, BestPairData>());
  return (
    <div className="card">
      <div className="card-body position-relative">
        <MapContainer
          crs={CRS.Simple}
          style={{ minHeight: '790px', height: `${mapHeight}px`, width: '100%' }}
          minZoom={-5}
          maxZoom={2.5}
          zoomSnap={0.1}
          zoomDelta={0.1}
          scrollWheelZoom={!isAnyModalOpen}
          doubleClickZoom={!isAnyModalOpen}
          dragging={!isAnyModalOpen}
        >
          <MapViewController initialViewRef={initialViewRef} imageBounds={imageBounds} planKey={currentPlan?.slug} />
          <MapWithRef />
          <MapInteractionController isAnyModalOpen={isAnyModalOpen} />
          {imageDimensions && (
            <MapUtilityButtons
              defaultCenter={[imageDimensions.height / 2, imageDimensions.width / 2]}
              defaultZoom={0}
              _mapHeight={mapHeight}
            />
          )}
          <ImageOverlay bounds={imageBounds!} url={currentPlan.floor_plan} />
          <MapController bounds={imageBounds!} />

          {isDrawingMode && (
            <AreaSelectionControl
              onChange={points => {
                setTempPolygon(points);
              }}
            />
          )}

          {polygons.map((poly, index) => {
            if (!poly.coordinates || poly.coordinates.length === 0) return null;
            const bounds = L.latLngBounds(poly.coordinates);

            return (
              <React.Fragment key={`polygon-group-${index}`}>
                <Polygon
                  pathOptions={{
                    color: poly.color,
                    weight: 5,
                    fillColor: poly.color,
                    fillOpacity: 0.4,
                  }}
                  positions={poly.coordinates}
                />
                <SVGOverlay bounds={bounds}>
                  <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" width="100%" height="100%">
                    <foreignObject x="0" y="0" width="100" height="100">
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          height: '100%',
                          padding: '2px',
                          boxSizing: 'border-box',
                        }}
                      >
                        <div
                          style={{
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                            wordWrap: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: '1.1',
                            width: '100%',
                          }}
                        >
                          {truncateText(poly.number, 40)} - {truncateText(poly.title, 50)}
                        </div>
                      </div>
                    </foreignObject>
                  </svg>
                </SVGOverlay>
              </React.Fragment>
            );
          })}

          {deviceToZoom && <ZoomToMarkerWithWaterRing position={[deviceToZoom.x, deviceToZoom.y]} />}
          {encToZoom && <ZoomToMarkerWithWaterRing position={[encToZoom.x_coord, encToZoom.y_coord]} />}

          {tempMarkers.map(tempMarker => {
            let deviceType: 'srbm' | 'mct' | 'srst' | 'smst' | 'hub' | 'vul' | 'enc' = 'mct';
            if (tempMarker.device.isHub || tempMarker.device.model?.toLowerCase().includes('hub')) {
              deviceType = 'hub';
            } else if (tempMarker.device.model?.toLowerCase().startsWith('srbm')) {
              deviceType = 'srbm';
            } else if (tempMarker.device.model?.toLowerCase().startsWith('mct')) {
              deviceType = 'mct';
            } else if (tempMarker.device.model?.toLowerCase().startsWith('srst')) {
              deviceType = 'srst';
            } else if (tempMarker.device.model?.toLowerCase().startsWith('smst')) {
              deviceType = 'smst';
            } else if (tempMarker.device.type === 'vul') {
              deviceType = 'vul';
            } else if (tempMarker.device.type === 'enc') {
              deviceType = 'enc';
            }

            return (
              <DeviceMarker
                refetchEncounters={refetchEncounters}
                key={`temp-${tempMarker.device.slug}`}
                device={tempMarker.device}
                position={tempMarker.position}
                deviceType={deviceType}
                isTemp
                plan_id={currentPlan.id}
                planSlug={currentPlan.slug}
                onTempMarkerSaved={() => handleTempMarkerSaved(tempMarker.device.slug)}
                markerRef={el => {
                  markerRefs.current[`temp_${tempMarker.device.slug}`] = el;
                  if (el && typeof el.openPopup === 'function') {
                    setTimeout(() => el.openPopup(), 100);
                  }
                }}
                objectSlug={slug}
                isActiveFilterArchive={isActiveFilterArchive}
              />
            );
          })}

          {filteredDevices.map(device => {
            const isArchivedGhost = isActiveFilterArchive && device.is_current_position === false;
            const markerOpacity = isArchivedGhost ? 0.5 : 1;
            if (isSnap(device.model)) {
              const key = pairKey(device);
              const baseKey = pairKey(device);
              const searchKey = device.place_point_id ? `${baseKey}_${device.place_point_id}` : baseKey;
              const best = bestByPair.get(searchKey);
              const isBest =
                best &&
                best.slug === device.slug &&
                (best.side ?? '').toUpperCase() === (device.trap_connected_module ?? '').toUpperCase();

              if (!isBest) return null;
              const overrideCaught = best.trippedCount >= 2 ? 2 : best.trippedCount >= 1 ? 1 : 0;

              return (
                <DeviceMarker
                  key={key}
                  device={{
                    ...device,
                    caught_pests: overrideCaught,
                    last_capture_1: best.lastCapture1 ?? null,
                    last_capture_2: best.lastCapture2 ?? null,
                    last_empty_1: best.lastEmpty1 ?? null,
                    last_empty_2: best.lastEmpty2 ?? null,
                    combine_history_triggers: best.combineHistoryTriggers ?? null,
                  }}
                  planSlug={currentPlan.slug}
                  position={[device.x, device.y]}
                  deviceType={
                    device.model.startsWith('srbm')
                      ? 'srbm'
                      : device.model.startsWith('mct')
                        ? 'mct'
                        : device.model.startsWith('srst')
                          ? 'srst'
                          : 'smst'
                  }
                  opacity={markerOpacity}
                  markerRef={el => (markerRefs.current[device.slug] = el)}
                  isActiveFilterArchive={isActiveFilterArchive}
                />
              );
            }

            return (
              <DeviceMarker
                key={device.slug}
                device={device}
                position={[device.x, device.y]}
                planSlug={currentPlan.slug}
                deviceType={
                  device.model.startsWith('srbm')
                    ? 'srbm'
                    : device.model.startsWith('mct')
                      ? 'mct'
                      : device.model.startsWith('srst')
                        ? 'srst'
                        : 'smst'
                }
                markerRef={el => (markerRefs.current[device.slug] = el)}
                opacity={markerOpacity}
                isActiveFilterArchive={isActiveFilterArchive}
              />
            );
          })}

          {hubs.map(hub => (
            <DeviceMarker
              key={hub.slug}
              device={hub}
              position={[hub.x, hub.y]}
              deviceType="hub"
              planSlug={currentPlan.slug}
              trapsConnected={
                Object.values(filteredDevices)
                  .flat()
                  .filter(d => d.hub_id === hub.id).length
              }
              mctTraps={
                Object.values(filteredDevices)
                  .flat()
                  .filter(d => d.model.startsWith('mct') && d.hub_id === hub.id).length
              }
              srbmTraps={
                Object.values(filteredDevices)
                  .flat()
                  .filter(d => d.model.startsWith('srbm') && d.hub_id === hub.id).length
              }
              srstTraps={
                Object.values(filteredDevices)
                  .flat()
                  .filter(d => d.model.startsWith('srst') && d.hub_id === hub.id).length
              }
              smstTraps={
                Object.values(filteredDevices)
                  .flat()
                  .filter(d => d.model.startsWith('smst') && d.hub_id === hub.id).length
              }
              markerRef={el => (markerRefs.current[hub.slug] = el)}
              hubCount={hubs.length}
              isActiveFilterArchive={isActiveFilterArchive}
            />
          ))}

          {filters.heatmap && heatmapData && filters.heatmapMct && (
            <HeatmapLayer points={heatmapData.map(point => [point.x, point.y, point.count])} deviceType="mct" />
          )}
          {filters.heatmap && filters.heatmapSRBM && heatmapSRBMData && (
            <HeatmapLayer points={heatmapSRBMData.map(point => [point[0], point[1], point[2]])} deviceType="srbm" />
          )}
          {filters.heatmap && filters.heatmapSMST && heatmapSMSTData && (
            <HeatmapLayer points={heatmapSMSTData.map(point => [point.x, point.y, point.count])} deviceType="smst" />
          )}
          {filters.heatmap && filters.heatmapSRST && heatmapSRSTData && (
            <HeatmapLayer points={heatmapSRSTData.map(point => [point.x, point.y, point.count])} deviceType="srst" />
          )}

          {filters.vulnerabilities &&
            vulnerabilities &&
            vulnerabilities.map(vul => {
              const isArchivedGhost = isActiveFilterArchive && vul.is_current_position === false;
              const markerOpacity = isArchivedGhost ? 0.5 : 1;
              return (
                <DeviceMarker
                  key={vul.id}
                  device={vul}
                  position={[vul.x_coord, vul.y_coord]}
                  deviceType="vul"
                  markerRef={el => (markerRefs.current[`vul_${vul.id}`] = el)}
                  objectSlug={slug || ''}
                  planSlug={currentPlan.slug}
                  opacity={markerOpacity}
                  isActiveFilterArchive={isActiveFilterArchive}
                />
              );
            })}

          {filters.encounters &&
            encounters &&
            encounters.map(enc => {
              const isArchivedGhost = isActiveFilterArchive && enc.is_current_position === false;
              const markerOpacity = isArchivedGhost ? 0.5 : 1;
              return (
                <DeviceMarker
                  key={`enc-${enc.id}`}
                  device={enc}
                  position={[enc.x_coord, enc.y_coord]}
                  deviceType="enc"
                  objectSlug={slug || ''}
                  refetchEncounters={refetchEncounters}
                  planSlug={currentPlan.slug}
                  opacity={markerOpacity}
                  isActiveFilterArchive={isActiveFilterArchive}
                />
              );
            })}

          <MapLegend />
          <PlanSelector
            slug={slug || ''}
            currentPlan={currentPlan}
            plans={plans || []}
            selectedPlanSlug={selectedPlanSlug}
            onPlanSelect={handlePlanSelect}
          />
          <AddMenuButton
            isActiveFilterArchive={isActiveFilterArchive}
            setIsAddMctModalOpen={setIsAddMctModalOpen}
            setIsAddSRBMModalOpen={setIsAddSRBMModalOpen}
            setIsAddSRSTModalOpen={setIsAddSRSTModalOpen}
            setIsAddSMSTModalOpen={setIsAddSMSTModalOpen}
            setIsAddHubModalOpen={setIsAddHubModalOpen}
            objectSlug={slug || ''}
            planSlug={currentPlan.slug}
          />
          <MapSettings
            devices={devices}
            filters={filters}
            onFilterChange={handleFilterChange}
            heatmapDateStart={heatmapDateStart}
            heatmapDateEnd={heatmapDateEnd}
            currentPlan={currentPlan}
            setHeatmapDateStart={date => {
              isArchiveFetchRef.current = true;
              setHeatmapDateStart(date);
            }}
            setHeatmapDateEnd={date => {
              isArchiveFetchRef.current = true;
              setHeatmapDateEnd(date);
            }}
            isHeatmapLoading={isHeatmapLoading}
            isHeatmapSRBMLoading={isHeatmapSRBMLoading}
            isHeatmapSMSTLoading={isHeatmapSMSTLoading}
            isHeatmapSRSTLoading={isHeatmapSRSTLoading}
            isHeatmapPopupOpen={isHeatmapPopupOpen}
            setIsHeatmapPopupOpen={setIsHeatmapPopupOpen}
            isOpen={isMapSettingsOpen}
            setIsOpen={value => {
              if (value === true) setIsLocationSettingsOpen(false);
              setIsMapSettingsOpen(value);
            }}
            setIsArchiveFilterActive={isActive => {
              isArchiveFetchRef.current = true;
              setIsActiveFilterArchive(isActive);
            }}
            setArchiveMode={setArchiveMode}
            devicesLoading={devicesLoading}
          />
          <TrapDeleteModal refetchTraps={refetchDevices} />
          <HubDeleteModal refetchHubs={refetchHubs} />
          <AddVulOnMapModal refetchVul={refetchVulnerabilities} />
          <AddEncounterOnMapModal refetchEncounters={refetchEncounters} />

          <AddMCTModal
            isOpen={isAddMctModalOpen}
            setIsOpen={setIsAddMctModalOpen}
            plan_slug={currentPlan.slug}
            obj_slug={slug || ''}
            onSetTempMarker={handleSetTempMarker}
          />
          <AddTrapModal
            isOpen={isAddSRSTModalOpen}
            setIsOpen={setIsAddSRSTModalOpen}
            plan_slug={currentPlan.slug}
            obj_slug={slug || ''}
            onSetTempMarker={handleSetTempMarker}
            type="srst"
          />
          <AddTrapModal
            isOpen={isAddSMSTModalOpen}
            setIsOpen={setIsAddSMSTModalOpen}
            plan_slug={currentPlan.slug}
            obj_slug={slug || ''}
            onSetTempMarker={handleSetTempMarker}
            type="smst"
          />
          <AddSRBMModal
            isOpen={isAddSRBMModalOpen}
            setIsOpen={setIsAddSRBMModalOpen}
            plan_slug={currentPlan.slug}
            obj_slug={slug || ''}
            onSetTempMarker={handleSetTempMarker}
          />
          <AddHubModal
            isOpen={isAddHubModalOpen}
            setIsOpen={setIsAddHubModalOpen}
            plan_slug={currentPlan.slug}
            obj_slug={slug || ''}
            onSetTempMarker={handleSetTempMarker}
            tempMarkers={tempMarkers}
          />

          <PutEncAfterBlitzModal refetchEncounters={refetchEncounters} />
          <CompleteEncModal refetchEncounters={refetchEncounters} vulnerabilities={vulnerabilities} />
          <CompleteVulModal refetchVulnerabilities={refetchVulnerabilities} />
          <EditPlanOnMapModal />
          <DeletePlanOnMapModal refetchTraps={refetchDevices} />
        </MapContainer>

        <LocationSettings
          refetchLocations={refetchLocations}
          location={polygons}
          planSlug={currentPlan.slug}
          tempPolygon={tempPolygon}
          locationSet={setPolygons}
          isOpen={isLocationSettingsOpen}
          setEditingIndex={setEditingIndex}
          setColorPolygon={setColorPolygon}
          setIsOpen={value => {
            if (value === true) setIsMapSettingsOpen(false);
            setIsLocationSettingsOpen(value);
          }}
          onToggleDrawMode={isActive => {
            setIsDrawingMode(isActive);
            if (!isActive) setTempPolygon([]);
          }}
          onConfirm={formData => {
            setPolygons((prev: MapLocation[]) => {
              if (editingIndex !== null) {
                return prev.map((poly, i) =>
                  i === editingIndex
                    ? {
                        ...poly,
                        coordinates: tempPolygon.length > 0 ? tempPolygon : poly.coordinates,
                        title: formData.title,
                        number: formData.number,
                        color: colorPolygon,
                      }
                    : poly,
                );
              }

              if (tempPolygon.length > 0) {
                return [
                  ...prev,
                  { coordinates: tempPolygon, color: colorPolygon, title: formData.title, number: formData.number },
                ];
              }
              return prev;
            });

            setTempPolygon([]);
            setEditingIndex(null);
            setIsDrawingMode(false);
            setIsLocationSettingsOpen(false);
          }}
        />
      </div>
    </div>
  );
};

export default InteractiveMap;
